import React, { useEffect } from "react";
import * as yup from 'yup';
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Spinner, FormGroup, Label, Input, FormFeedback, Alert } from "reactstrap";

const schema = yup.object().shape({
  credits: yup
    .number()
    .typeError('Credits must be a number')
    .required('Credits amount is required')
    .positive('Credits must be a positive number')
    .integer('Credits must be a whole number')
    .min(1, 'Credits must be at least 1'),
  creditMode: yup
    .string()
    .oneOf(['add', 'replace'], 'Select add or replace')
    .required('Add or replace is required'),
});

const AddMatchCreditsModal = ({ open, onClose, organization, onSubmit }) => {
  const { control, reset, handleSubmit, formState: { isSubmitting, errors } } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      credits: '',
      creditMode: 'add',
    }
  });

  useEffect(() => {
    if (open) {
      reset({ credits: '', creditMode: 'add' });
    }
  }, [open, reset]);

  const handleFormSubmit = async (values) => {
    try {
      await onSubmit(organization, parseInt(values.credits), values.creditMode);
      reset();
      onClose();
    } catch (e) {
      console.error('Error adding match credits:', e);
    }
  };

  const currentMatchCredits = organization?.plan_details?.max_credits ?? 0;

  return (
    <Modal isOpen={open} toggle={onClose} centered>
      <ModalHeader toggle={onClose}>
        <span style={{ fontSize: "18px", fontWeight: "700" }}>
          Add Credits
        </span>
      </ModalHeader>
      <ModalBody>
        {organization && (
          <Alert color="info" className="mb-3">
            <div><strong>Organization:</strong> {organization.name || 'N/A'}</div>
            {organization.type && (
              <div><strong>Organization Type:</strong> {organization.type}</div>
            )}
            <div><strong>Current Match Credits (max):</strong> {currentMatchCredits}</div>
          </Alert>
        )}

        <FormGroup>
          <Label className="d-block mb-2" style={{ fontWeight: '600' }}>Add or replace existing credits *</Label>
          <Controller
            name="creditMode"
            control={control}
            render={({ field: { value, onChange } }) => (
              <div className="d-flex gap-4">
                <div className="form-check">
                  <Input
                    type="radio"
                    id="credit_mode_add"
                    name="creditMode"
                    value="add"
                    checked={value === 'add'}
                    onChange={(e) => onChange(e.target.value)}
                    className="form-check-input"
                  />
                  <Label className="form-check-label" htmlFor="credit_mode_add" style={{ cursor: 'pointer' }}>
                    Add
                  </Label>
                </div>
                <div className="form-check">
                  <Input
                    type="radio"
                    id="credit_mode_replace"
                    name="creditMode"
                    value="replace"
                    checked={value === 'replace'}
                    onChange={(e) => onChange(e.target.value)}
                    className="form-check-input"
                  />
                  <Label className="form-check-label" htmlFor="credit_mode_replace" style={{ cursor: 'pointer' }}>
                    Replace
                  </Label>
                </div>
              </div>
            )}
          />
          {errors?.creditMode && (
            <FormFeedback style={{ display: 'block' }}>{errors.creditMode.message}</FormFeedback>
          )}
        </FormGroup>

        <FormGroup>
          <Label>Credits to Add *</Label>
          <Controller
            name="credits"
            control={control}
            render={({ field: { value, onChange } }) => (
              <Input
                type="number"
                value={value}
                onChange={onChange}
                invalid={!!errors?.credits}
                placeholder="Enter number of credits"
                min="1"
                step="1"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSubmit(handleFormSubmit)();
                  }
                  if (e.key === '.' || e.key === '-' || e.key === 'e' || e.key === 'E') {
                    e.preventDefault();
                  }
                }}
              />
            )}
          />
          {errors?.credits && (
            <FormFeedback style={{ display: 'block' }}>{errors.credits.message}</FormFeedback>
          )}
        </FormGroup>
      </ModalBody>
      <ModalFooter>
        <button
          className='btn btn-outline-dark'
          onClick={onClose}
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <Button
          color="primary"
          onClick={handleSubmit(handleFormSubmit)}
          type='submit'
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <Spinner size="sm" color="light" />
          ) : (
            "Add Credits"
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AddMatchCreditsModal;
